import React, { FC, useCallback, useEffect, useState } from "react";
import { Column } from "react-table";
import {
  Container,
  Button,
  DialogContent,
  Grid,
  makeStyles,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import FormDialog from "../../components/dialogs/FormDialog";
import Table from "../../components/Table";
import NoDataDialog from "../../components/dialogs/ErrorDialog";
import APIRequest from "../../components/helpers/APIRequest";
import DividerWithText from "../../components/layout/dividerwithtext";
import * as UserAPI from "../../api/usermanagement/users";
import * as UserModels from "../../api/usermanagement/models";
import * as ShortAPI from "../../api/short/index";
import * as ShortModels from "../../api/short/models";
import * as CSV from "../../utils/exportcsv";
import moment from "moment";
import "moment/locale/da";

moment.locale("da");

const useStyles = makeStyles((theme) => ({
  paper: {
    [theme.breakpoints.up("sm")]: {
      padding: "0",
    },
    marginTop: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  buts: {
    width: "100%",
    height: "100%",
    margin: "8px",
  },
  grid: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  textHelp: {
    marginBottom: "1rem",
  },
  margTop: {
    marginTop: theme.spacing(2),
  },
}));

const Users: FC = () => {
  const classes = useStyles();
  const [users, setUsers] = useState<UserModels.UsersData>(
    new UserModels.UsersData()
  );
  const [isPendingGetUsers, setPendingGetUsers] = useState(false);
  const [runGetUsers, setRunGetUsers] = useState(true);

  const [showNoDataDialog, setShowNoDataDialog] = React.useState(false);
  const [showErrorDialog, setShowErrorDialog] = React.useState(0);

  // Location data
  const [locationData, setLocationData] = useState<ShortModels.Location[]>([]);
  const [sortedLocationData, setSortedLocationData] = useState<
    ShortModels.Location[]
  >([]);
  const [isPendingLocationData, setIsPendingLocationData] = useState(false);
  const [runRequestLocationData, setRunRequestLocationData] = useState(true);

  // Edit user dialog control
  const [runStoreUser, setRunStoreUser] = useState(false);
  const [isPendingStoreUsers, setPendingStoreUsers] = useState(false);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editDialogTitle, setEditDialogTitle] = useState("");

  const [showOnlyActiveUsers, setShowOnlyActiveUsers] = useState(true);

  // User definition
  const [userId, setUserId] = useState<number>(0);
  const [userUsername, setUserUsername] = useState("");
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userTitle, setUserTitle] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [userActive, setUserActive] = useState(false);
  const [userPermissionAlarms, setUserPermissionAlarms] = useState(false);
  const [userPermissionDoorOpener, setUserPermissionDoorOpener] =
    useState(false);
  const [userPermissionGlobalSearch, setUserPermissionGlobalSearch] =
    useState(false);
  const [userPermissionInfoModule, setUserPermissionInfoModule] =
    useState(false);
  const [userPermissionMobileBase, setUserPermissionMobileBase] =
    useState(false);
  const [userPermissionStatistics, setUserPermissionStatistics] =
    useState(false);
  const [userPermissionUserManagement, setUserPermissionUserManagement] =
    useState(false);
  const [userPermissionDigitalLicenses, setUserPermissionDigitalLicenses] =
    useState(false);
  const [userLocations, setUserLocations] = useState<ShortModels.Location[]>(
    []
  );

  useEffect(() => {
    if (!locationData || locationData.length < 1) {
      setSortedLocationData([]);
    }

    let data: ShortModels.Location[] = [{ id: -1, name: "Alle" }];
    data = data.concat(
      locationData.sort((a, b) => {
        return a.name.localeCompare(b.name);
      })
    );
    setSortedLocationData(data);
  }, [locationData]);

  const renderPermissionsCell = (user: UserModels.User) => {
    let u: string[] = [];
    if (user.permissionAlarms) {
      u.push("alarmer");
    }
    if (user.permissionDoorOpener) {
      u.push("SMS døråbner");
    }
    if (user.permissionGlobalSearch) {
      u.push("parkantopslag");
    }
    if (user.permissionInfoModule) {
      u.push("infomodul");
    }
    if (user.permissionMobileBase) {
      u.push("mobilbase");
    }
    if (user.permissionStatistics) {
      u.push("statistik");
    }
    if (user.permissionUserManagement) {
      u.push("brugerhåndtering");
    }
    if (user.permissionDigitalLicenses) {
      u.push("digitale licenser");
    }
    var s = u.join(", ");
    if (s.length > 0) {
      return s.charAt(0).toUpperCase() + s.slice(1);
    }
    // No permissions
    return "-";
  };

  const columns: Column<UserModels.User>[] = React.useMemo(
    () => [
      { Header: "Brugernavn", id: "0", accessor: "username" },
      { Header: "Navn", id: "1", accessor: "name" },
      { Header: "Email", id: "2", accessor: "email" },
      { Header: "Titel", id: "3", accessor: "title" },
      { Header: "Mobiltelefon", id: "4", accessor: "phone" },
      {
        Header: "Aktiv",
        id: "5",
        accessor: "active",
        Cell: (c) => (c.value ? "Ja" : "Nej"),
      },
      {
        Header: "Rettigheder",
        id: "6",
        accessor: (user) => renderPermissionsCell(user),
      },
    ],
    []
  );

  const handleClickNewUser = () => {
    // Run the dialog with a new user
    handleRowClick(new UserModels.User());
  };

  const sortAndSetUserLocations = (locations: ShortModels.Location[]) => {
    if (locations.length === 1 && locations[0].id === -1) {
      // User clicked the all button with nothing selected
      locations = locationData;
    } else if (locations.find((l) => l.id === -1)) {
      // The all button was pressed
      if (locations.length < sortedLocationData.length) {
        // Not all locations were previously checked, choose all
        locations = locationData;
      } else {
        // Everything was checked, so now we should clear it
        locations = [];
      }
    }
    locations.sort((a, b) => a.name.localeCompare(b.name));
    setUserLocations(locations);
  };

  const handleRowClick = (element: UserModels.User) => {
    if (element.id === 0) {
      setEditDialogTitle("Ny bruger");
    } else {
      setEditDialogTitle("Ændring af bruger");
    }

    setUserId(element.id);
    setUserUsername(element.username);
    setUserName(element.name.trim());
    setUserEmail(element.email);
    setUserTitle(element.title);
    setUserPhone(element.phone);
    setUserPassword(""); // The password should always be empty, even for editing current users. Will be set if it's non-empty
    setUserActive(element.active);
    setUserPermissionAlarms(element.permissionAlarms);
    setUserPermissionDoorOpener(element.permissionDoorOpener);
    setUserPermissionGlobalSearch(element.permissionGlobalSearch);
    setUserPermissionInfoModule(element.permissionInfoModule);
    setUserPermissionMobileBase(element.permissionMobileBase);
    setUserPermissionStatistics(element.permissionStatistics);
    setUserPermissionUserManagement(element.permissionUserManagement);
    setUserPermissionDigitalLicenses(element.permissionDigitalLicenses);

    // Set the locations list
    let locations: ShortModels.Location[] = [];
    element.locationsAccess.forEach((location) => {
      var ld = locationData.find((l) => l.id === location);
      if (ld) {
        locations.push(ld);
      }
    });
    sortAndSetUserLocations(locations);

    setEditUserOpen(true);
  };

  const downloadCSV = () => {
    let headers: string[] = columns.map((column) => {
      return "" + column.Header;
    });
    let keys: string[] = columns.map((column) => {
      return "" + column.accessor;
    });
    CSV.exportUsers(
      "users.csv",
      headers,
      keys,
      users.result.users,
      [],
      [2],
      users,
      locationData
    );
  };

  const RequestUsers = () => {
    return (
      <APIRequest<UserModels.UsersData>
        run={runGetUsers}
        setRun={setRunGetUsers}
        pending={isPendingGetUsers}
        setIsPending={setPendingGetUsers}
        req={() => UserAPI.getUsers()}
        setData={setUsers}
        setShowError={setShowNoDataDialog}
      />
    );
  };

  const StoreUser = () => {
    const createUserParam = () => {
      return new UserModels.StoreParams(
        userUsername ? userUsername.trim() : "",
        userName ? userName.trim() : "",
        userEmail ? userEmail.trim() : "",
        userTitle ? userTitle.trim() : "",
        userPhone ? userPhone.trim() : "",
        userPassword ? userPassword.trim() : "",
        userActive,
        userPermissionAlarms,
        userPermissionDoorOpener,
        userPermissionGlobalSearch,
        userPermissionInfoModule,
        userPermissionMobileBase,
        userPermissionStatistics,
        userPermissionUserManagement,
        userPermissionDigitalLicenses,
        userLocations.map((l) => l.id)
      );
    };
    return (
      <APIRequest<UserModels.UsersData>
        run={runStoreUser}
        setRun={setRunStoreUser}
        pending={isPendingStoreUsers}
        setIsPending={setPendingStoreUsers}
        req={() => UserAPI.storeUser(userId, createUserParam())}
        setData={setUsers}
        storeError={setShowErrorDialog}
      />
    );
  };

  const RequestLocationData = () => {
    return (
      <APIRequest<ShortModels.Location[]>
        run={runRequestLocationData}
        setRun={setRunRequestLocationData}
        pending={isPendingLocationData}
        setIsPending={setIsPendingLocationData}
        req={ShortAPI.getAllLocations}
        setData={setLocationData}
      />
    );
  };

  // Used in the dialog to update the list of locations
  const setLocationSelection = (selection: ShortModels.Location[] | null) => {
    sortAndSetUserLocations(selection ? selection : []);
  };

  const activeFilter = useCallback(
    (users: UserModels.User[]) => {
      if (showOnlyActiveUsers) {
        return users.filter((user) => user.active);
      }
      return users;
    },
    [showOnlyActiveUsers]
  );

  const submitStoreUser = () => {
    if (userUsername.trim().length === 0) {
      setShowErrorDialog(-1);
    } else if (userName.trim().length === 0) {
      setShowErrorDialog(-2);
    } else if (userId === 0 && userEmail.trim().length === 0) {
      setShowErrorDialog(-3);
    } else {
      setRunStoreUser(true);
      setEditUserOpen(false);
    }
  };

  const errorMessage = () => {
    switch (showErrorDialog) {
      case -1:
        return "Brugernavn må ikke være tomt";
      case -2:
        return "Navn må ikke være tomt";
      case -3:
        return "Email må ikke være tom for nye brugere";

      case 406:
        return "Ukendt fejl, kunne ikke gemme oplysningerne. Prøv igen senere.";
      case 409:
        return "Dette brugernavn er optaget, prøv et andet.";

      case 0:
        return ""; // No error
    }
    return "Der er sket en fejl. Prøv igen senere.";
  };
  const inputRef = React.useRef<any>(null);
  const test = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <Container maxWidth={false}>
      <RequestUsers />
      <RequestLocationData />
      <StoreUser />
      <div className={classes.paper}>
        <Typography component="h1" variant="h5">
          Brugerhåndtering
        </Typography>
        <Container maxWidth={"xs"} className={classes.margTop}>
          <Button
            variant="contained"
            color="primary"
            className={classes.buts}
            onClick={handleClickNewUser}
          >
            Ny bruger
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.buts}
            onClick={() => setRunGetUsers(true)}
          >
            Opdatér liste
          </Button>
          <Button
            disabled={users.result.users.length < 1}
            variant="contained"
            color="primary"
            className={classes.buts}
            onClick={downloadCSV}
          >
            Eksportér
          </Button>
          <FormControlLabel
            control={
              <Checkbox
                style={{ marginLeft: "7px" }}
                name="activeUsers"
                color="primary"
                id="activeUsers"
                checked={showOnlyActiveUsers}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setShowOnlyActiveUsers(e.currentTarget.checked);
                }}
              />
            }
            label="Vis kun aktive brugere"
          />
        </Container>
        <div className={classes.grid}>
          <Table<UserModels.User>
            inputFullWidth
            columns={columns}
            data={activeFilter(users.result.users)}
            initialState={{ pageSize: 150 }}
            clickEvent={handleRowClick}
            update={null}
            pending={false}
            text={""}
          />
        </div>
      </div>

      <NoDataDialog
        open={showNoDataDialog}
        onClose={() => {
          setShowNoDataDialog(false);
        }}
        title={"Intet svar"}
        text={"Noget gik galt, prøv venligst igen senere."}
      />

      <NoDataDialog
        open={showErrorDialog !== 0}
        onClose={() => {
          setShowErrorDialog(0);
          setEditUserOpen(true);
        }}
        title={"Fejl"}
        text={errorMessage()}
      />

      <FormDialog
        title={editDialogTitle}
        open={editUserOpen}
        onCloseCancel={() => setEditUserOpen(false)}
        onCloseSubmit={submitStoreUser}
      >
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                autoComplete="username"
                name="username"
                variant="outlined"
                fullWidth
                id="username"
                label="Brugernavn"
                value={userUsername}
                autoFocus
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setUserUsername(e.currentTarget.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                autoComplete="name"
                name="name"
                variant="outlined"
                fullWidth
                id="name"
                label="Navn"
                value={userName}
                autoFocus
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setUserName(e.currentTarget.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                autoComplete="email"
                name="email"
                variant="outlined"
                fullWidth
                id="email"
                label="Email"
                value={userEmail}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setUserEmail(e.currentTarget.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                autoComplete="title"
                name="title"
                variant="outlined"
                fullWidth
                id="title"
                label="Titel"
                value={userTitle}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setUserTitle(e.currentTarget.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                autoComplete="phone"
                name="phone"
                variant="outlined"
                fullWidth
                id="phone"
                label="Mobiltelefon"
                value={userPhone}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setUserPhone(e.currentTarget.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                autoComplete="password"
                type="password"
                name="password"
                variant="outlined"
                fullWidth
                id="password"
                label="Password"
                value={userPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setUserPassword(e.currentTarget.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="active"
                    id="active"
                    checked={userActive}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setUserActive(e.currentTarget.checked);
                    }}
                  />
                }
                label="Aktiv"
              />
              <DividerWithText>Rettigheder</DividerWithText>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="permission_alarms"
                    id="permission_alarms"
                    label="Alarmer"
                    checked={userPermissionAlarms}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setUserPermissionAlarms(e.currentTarget.checked);
                    }}
                  />
                }
                label="Alarmer"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={!users.result.permissions.doorOpener}
                    name="permission_dooropener"
                    id="permission_dooropener"
                    label="SMS døråbner"
                    checked={userPermissionDoorOpener}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setUserPermissionDoorOpener(e.currentTarget.checked);
                    }}
                  />
                }
                label="SMS døråbner"
              />
            </Grid>
            {/*  <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={<Checkbox disabled={!users.result.permissions.globalSearch} name="permission_globalSearch" id="permission_globalSearch" label="Parkantopslag" checked={userPermissionGlobalSearch} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setUserPermissionGlobalSearch(e.currentTarget.checked)}} />}
                                    label="Parkantopslag" />
                            </Grid> */}
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={!users.result.permissions.infoModule}
                    name="permission_infomodul"
                    id="permission_infomodul"
                    label="Infomodul"
                    checked={userPermissionInfoModule}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setUserPermissionInfoModule(e.currentTarget.checked);
                    }}
                  />
                }
                label="Infomodul"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="permission_mobilbase"
                    id="permission_mobilbase"
                    label="Mobilbase"
                    checked={userPermissionMobileBase}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setUserPermissionMobileBase(e.currentTarget.checked);
                    }}
                  />
                }
                label="Mobilbase"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="permission_stats"
                    id="permission_stats"
                    label="Statistik"
                    checked={userPermissionStatistics}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setUserPermissionStatistics(e.currentTarget.checked);
                    }}
                  />
                }
                label="Statistik"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="permission_users"
                    id="permission_users"
                    label="Brugerhåndtering"
                    checked={userPermissionUserManagement}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setUserPermissionUserManagement(e.currentTarget.checked);
                    }}
                  />
                }
                label="Brugerhåndtering"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={!users.result.permissions.digitalLicenses}
                    name="permission_digital_licenses"
                    id="permission_digital_licenses"
                    label="Digitale licenser"
                    checked={userPermissionDigitalLicenses}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setUserPermissionDigitalLicenses(e.currentTarget.checked);
                    }}
                  />
                }
                label="Digitale licenser"
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Autocomplete
                ref={inputRef}
                multiple
                limitTags={3}
                value={userLocations}
                options={sortedLocationData}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Pladsadgang"
                    variant="outlined"
                    fullWidth
                  />
                )}
                onChange={(
                  event: any,
                  newSelection: ShortModels.Location[] | null
                ) => setLocationSelection(newSelection)}
                renderOption={(option, state) => {
                  return (
                    <React.Fragment>
                      <Checkbox
                        indeterminate={
                          option.id === -1 &&
                          userLocations.length > 0 &&
                          userLocations.length < locationData.length
                        }
                        checked={
                          (option.id !== -1 && state.selected) ||
                          (option.id === -1 &&
                            userLocations.length > 0 &&
                            userLocations.length === locationData.length)
                        }
                      />
                      {option.name}
                    </React.Fragment>
                  );
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
      </FormDialog>
    </Container>
  );
};

export default Users;
